<template>
  <div class="apple-wallpaper-settings" :class="{ 'is-advanced-open': advancedOpen }">
    <div class="apple-wallpaper-main">
    <div class="apple-wallpaper-card">
    <div class="apple-wallpaper-overview">
      <div class="apple-wallpaper-preview">
        <AppleArtwork :src="preview" :fallback="fallback" :retry-key="wallpaper.url" alt="当前背景预览" size="100%" />
      </div>
      <div class="apple-wallpaper-copy">
        <strong>背景壁纸</strong>
        <p>{{ wallpaper.name ? `当前：${wallpaper.name}` : '默认使用当前歌曲封面' }}</p>
        <div class="apple-wallpaper-actions">
          <AppleButton :disabled="wallpaper.busy" @click="$refs.file.click()">选择图片</AppleButton>
          <AppleButton variant="subtle" :disabled="wallpaper.busy || !wallpaper.url" @click="clear">恢复默认</AppleButton>
        </div>
      </div>
    </div>
    <input ref="file" class="apple-wallpaper-file" type="file" accept="image/*" aria-label="选择背景壁纸" @change="select" />
    <p v-if="wallpaper.error" class="apple-wallpaper-error" role="alert">{{ wallpaper.error }}</p>
    <button type="button" class="apple-wallpaper-advanced-entry" :aria-expanded="String(advancedOpen)" aria-controls="apple-wallpaper-advanced-options" @click="advancedOpen = !advancedOpen"><span>高级背景选项</span><span aria-hidden="true">›</span></button>
    </div>
    <slot name="control-bar" />
    </div>
    <section v-if="advancedOpen" id="apple-wallpaper-advanced-options" class="apple-wallpaper-advanced" aria-labelledby="apple-wallpaper-advanced-title">
      <h3 id="apple-wallpaper-advanced-title">高级背景选项</h3>
      <div class="apple-wallpaper-options">
        <AppleSettingRow title="壁纸模糊程度" :description="wallpaper.customBlur ? '自定义模糊程度' : '跟随界面材质'">
          <template #default="row"><div class="apple-wallpaper-range"><AppleSlider :aria-labelledby="row.labelId" :aria-describedby="row.descriptionId" :min="0" :max="240" :step="5" :value="wallpaper.blur" @input="setBlur" /><output>{{ wallpaper.customBlur ? wallpaper.blur + ' px' : '默认' }}</output><AppleButton v-if="wallpaper.customBlur" variant="plain" @click="resetBlur">重置</AppleButton></div></template>
        </AppleSettingRow>
        <AppleSettingRow title="背景流光" description="缓慢移动的光影；遵循系统减少动态效果设置。" @activate="toggleFlow(!wallpaper.flow)">
          <template #default="row"><AppleSwitch :aria-labelledby="row.labelId" :aria-describedby="row.descriptionId" :value="wallpaper.flow > 0" @change="toggleFlow" /></template>
        </AppleSettingRow>
        <AppleSettingRow v-if="wallpaper.flow > 0" title="流光强度">
          <template #default="row"><div class="apple-wallpaper-range"><AppleSlider :aria-labelledby="row.labelId" :min="0" :max="100" :step="5" :value="wallpaper.flow" @input="setFlow" /><output>{{ wallpaper.flow }}%</output></div></template>
        </AppleSettingRow>
        <AppleSettingRow title="沉浸背景动态效果" description="遵循系统的减少动态效果设置。">
          <template #default="row"><AppleSelect :aria-labelledby="row.labelId" :aria-describedby="row.descriptionId" :value="state.motionLevel" @change="setMotion($event.target.value)"><option value="high">高</option><option value="medium">中</option><option value="low">低</option><option value="off">关闭</option></AppleSelect></template>
        </AppleSettingRow>
        <AppleSettingRow title="背景设置保存位置" description="模糊和流光可同步；壁纸图片保留在此设备。">
          <template #default="row"><AppleSelect :aria-labelledby="row.labelId" :aria-describedby="row.descriptionId" :value="preference.state.storage" :disabled="preference.state.busy" @change="storage($event.target.value)"><option value="local">此浏览器本地</option><option value="cloud" :disabled="!site.user">云端账号{{ site.user ? '' : '（请先登录）' }}</option></AppleSelect></template>
        </AppleSettingRow>
        <p v-if="preference.state.error" class="apple-wallpaper-error" role="status">{{ preference.state.error }}</p>
      </div>
    </section>
  </div>
</template>

<script>
import WallpaperSettings from '../salt/WallpaperSettings.vue';
import { runtime } from '../bridge';
import AppleArtwork from './ui/AppleArtwork.vue';
import AppleButton from './ui/AppleButton.vue';
import AppleSelect from './ui/AppleSelect.vue';
import AppleSettingRow from './ui/AppleSettingRow.vue';
import AppleSlider from './ui/AppleSlider.vue';
import AppleSwitch from './ui/AppleSwitch.vue';

export default {
  name: 'AppleWallpaperSettings',
  extends: WallpaperSettings,
  components: { AppleArtwork, AppleButton, AppleSelect, AppleSettingRow, AppleSlider, AppleSwitch },
  data() { return { state: runtime.state, lastFlow: 30, advancedOpen:false }; },
  computed: {
    fallback() { return runtime.cover(); },
    preview() {
      const track = this.state.tracks.find(item => item.id === this.state.currentId);
      return this.wallpaper.url || (track ? runtime.cover(track) : this.fallback);
    },
  },
  watch: { 'wallpaper.flow': { immediate: true, handler(value) { if (value > 0) this.lastFlow = value; } } },
  methods: {
    toggleFlow(enabled) { this.setFlow(enabled ? this.lastFlow || 30 : 0); },
    setMotion(value) { runtime.setMotionLevel(value); },
  },
};
</script>

<style scoped>
.apple-wallpaper-settings { min-width:0; }
.apple-wallpaper-main { display:grid; align-content:start; gap:16px; min-width:0; }
.apple-wallpaper-card { min-width:0; overflow:hidden; }
.apple-wallpaper-overview { display:flex; align-items:center; gap:14px; min-width:0; padding:12px; }
.apple-wallpaper-preview { flex:0 0 38%; width:38%; aspect-ratio:1.6; overflow:hidden; border-radius:6px; }
.apple-wallpaper-preview ::v-deep .apple-ui-artwork { width:100%; height:100%; }
.apple-wallpaper-copy { flex:1; min-width:0; }
.apple-wallpaper-copy strong { font-size:13px; font-weight:600; }
.apple-wallpaper-copy p { margin:4px 0 9px; color:var(--apple-ui-muted,var(--color-secondary)); font-size:12px; line-height:1.4; overflow-wrap:anywhere; }
.apple-wallpaper-actions { display:flex; flex-wrap:wrap; gap:8px; }
.apple-wallpaper-file { position:absolute; width:1px; height:1px; overflow:hidden; clip-path:inset(50%); }
.apple-wallpaper-error { margin:6px 0 10px; color:var(--apple-ui-error,#c42b1c); font-size:12px; line-height:1.45; overflow-wrap:anywhere; }
[data-theme='dark'] .apple-wallpaper-error { color:var(--apple-ui-error,#ff7b86); }
.apple-wallpaper-advanced-entry { display:flex; align-items:center; justify-content:space-between; width:100%; min-height:32px; padding:6px 12px; border:0; border-top:1px solid var(--apple-ui-border); background:transparent; color:var(--apple-ui-text); font:inherit; font-size:12px; text-align:left; cursor:pointer; }
.apple-wallpaper-advanced-entry > span:last-child { color:var(--apple-ui-muted); font-size:20px; line-height:1; transition:transform .15s ease; }
.apple-wallpaper-advanced-entry[aria-expanded='true'] > span:last-child { transform:rotate(90deg); }
.apple-wallpaper-advanced-entry:hover { background:var(--apple-ui-hover); }
.apple-wallpaper-advanced-entry:focus-visible { outline:none; box-shadow:inset 0 0 0 2px var(--apple-ui-focus); }
.apple-wallpaper-advanced h3 { margin:0; padding:12px 0 4px; font-size:13px; font-weight:600; }
.apple-wallpaper-options > .apple-ui-setting-row + .apple-ui-setting-row { border-top:1px solid var(--apple-ui-border,var(--apple-border)); }
.apple-wallpaper-range { display:flex; align-items:center; justify-content:flex-end; gap:8px; min-width:0; }
.apple-wallpaper-range .apple-ui-slider { width:clamp(72px,12vw,160px); }
.apple-wallpaper-range output { min-width:42px; color:var(--apple-ui-muted,var(--color-secondary)); font-size:12px; text-align:right; white-space:nowrap; font-variant-numeric:tabular-nums; }
@media (max-width:600px) { .apple-wallpaper-overview { gap:12px; } }
@media (prefers-reduced-motion:reduce) { .apple-wallpaper-advanced-entry > span:last-child { transition:none; } }
</style>
